import type { RpSectionOf } from '@mlm/shared';
import { cn } from '../../lib/cn';
import { RpBullets, RpLinkButton, RpMedia, RpParagraphs } from '../primitives';

/**
 * A picture or video beside a block of copy — the 2-up workhorse.
 *
 * This section plus `featureGrid` is why the builder has no generic nestable
 * `columns` container: between them they cover essentially every side-by-side
 * layout a landing page needs, without a tree editor.
 *
 * Stacks to a single column below `lg`. `mediaLeft` uses grid `order` rather
 * than reversing the DOM, so the copy always precedes the image for screen
 * readers and on mobile.
 */
export function RpMediaText({ section }: { section: RpSectionOf<'mediaText'> }) {
  const c = section.config;
  const mediaFirst = c.variant === 'mediaLeft';
  return (
    <div className="grid items-center gap-8 lg:grid-cols-2">
      <div className={cn('space-y-4', mediaFirst && 'lg:order-2')}>
        {c.eyebrow ? (
          <p className="text-xs font-semibold uppercase tracking-widest opacity-70">{c.eyebrow}</p>
        ) : null}
        {c.heading ? (
          <h2 className="font-heading text-2xl font-bold sm:text-3xl">{c.heading}</h2>
        ) : null}
        <RpParagraphs text={c.body} />
        <RpBullets items={c.bullets} />
        {c.cta ? (
          <div className="pt-1">
            <RpLinkButton button={c.cta} />
          </div>
        ) : null}
      </div>
      <RpMedia media={c.media} className={cn('w-full', mediaFirst && 'lg:order-1')} />
    </div>
  );
}
